// Rules for the tenant document library shared by the backend (upload, quote save, mail send)
// and the browser apps (picker meter, settings form). Dependency-free on purpose: the portal
// imports this file directly (`@phrospr/lib-core/document`), so it must never pull the rest of
// the barrel (moment, decimal.js) along.

import {
    DOCUMENT_LABEL_MAX_LENGTH,
    DocumentLanguage,
    IDocumentEntity,
    QUOTE_MAIL_ATTACHMENT_BUDGET_CEILING_BYTES,
} from '@phrospr/lib-models';

export interface IAttachmentSize {
    name: string;
    size_bytes: number;
}

export interface IAttachmentBudgetSummary {
    total_bytes: number;
    budget_bytes: number;
    over_bytes: number;
    within_budget: boolean;
}

export function normalizeDocumentLabel(label: unknown): string | null {
    if (typeof label !== 'string') return null;
    const trimmed = label.trim().replace(/\s+/g, ' ');
    if (!trimmed.length || trimmed.length > DOCUMENT_LABEL_MAX_LENGTH) return null;
    return trimmed;
}

export function labelFromFileName(fileName: string): string {
    return (fileName ?? '').replace(/\.[^.]+$/, '').trim() || 'document';
}

export function isDocumentLanguage(value: unknown): value is DocumentLanguage {
    return typeof value === 'string' && Object.values(DocumentLanguage).includes(value as DocumentLanguage);
}

// Language hint from the file names the catalogue grew with ("AG400-NL.pdf", "AG Pure_fr.pdf",
// "Leaflet_EN_print.pdf", "Nederlands", "Frans"). Null when nothing matches.
export function guessDocumentLanguage(fileName: string): DocumentLanguage | null {
    const base = labelFromFileName(fileName);
    if (/nederlands/i.test(base)) return DocumentLanguage.nl;
    if (/frans/i.test(base)) return DocumentLanguage.fr;
    const tokens = base.split(/[^A-Za-z]+/).map(token => token.toLowerCase());
    if (tokens.includes('nl')) return DocumentLanguage.nl;
    if (tokens.includes('fr')) return DocumentLanguage.fr;
    if (tokens.includes('en') || tokens.includes('eng')) return DocumentLanguage.en;
    return null;
}

// A PDF starts with "%PDF-"; the client's content type is not trusted.
export function looksLikePdf(bytes: Uint8Array): boolean {
    if (!bytes || bytes.length < 5) return false;
    const head = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3], bytes[4]);
    return head === '%PDF-';
}

export function isValidAttachmentBudget(bytes: unknown): bytes is number {
    return (
        typeof bytes === 'number' &&
        Number.isInteger(bytes) &&
        bytes > 0 &&
        bytes <= QUOTE_MAIL_ATTACHMENT_BUDGET_CEILING_BYTES
    );
}

export function summarizeAttachmentBudget(
    attachments: IAttachmentSize[],
    budgetBytes: number,
): IAttachmentBudgetSummary {
    const total_bytes = attachments.reduce((sum, attachment) => sum + (attachment.size_bytes ?? 0), 0);
    const over_bytes = Math.max(0, total_bytes - budgetBytes);
    return { total_bytes, budget_bytes: budgetBytes, over_bytes, within_budget: over_bytes === 0 };
}

// The documents a mail of a category carries: the ones selected on the record plus every active
// always-attach document, each once, in catalogue order. A selected document that was archived
// since stays (the client gets what was chosen), an archived always-attach one does not.
export function resolveMailDocuments(selected: IDocumentEntity[], library: IDocumentEntity[]): IDocumentEntity[] {
    const byId = new Map<number, IDocumentEntity>();
    for (const document of selected) byId.set(document.document_id, document);
    for (const document of library) {
        if (document.always_attach && document.active) byId.set(document.document_id, document);
    }
    return [...byId.values()].sort((a, b) => a.sequence - b.sequence || a.document_id - b.document_id);
}

export function formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
